import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone, Camera, FileText, Calendar, Shield, ArrowRight, Award, Hammer, Sun, Zap } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { SectionHeading, FinalCTA, CTA } from "@/components/Shared";
import { SEO, breadcrumbSchema } from "@/components/SEO";
import { DarkHeroAccent, RoofAccent } from "@/components/RoofAccents";

export function FreeRoofInspectionPage() {
  const steps = [
    { icon: Phone, t: "Call or submit online", d: "Call (248) 804-6933 or book online. We respond same day during business hours." },
    { icon: Calendar, t: "Schedule your inspection", d: "We pick a time that works - evenings and Saturdays available across Oakland County." },
    { icon: Camera, t: "On-site walk-up inspection", d: "Our lead installer walks the roof, checks flashing, valleys, vents, and attic ventilation. We photograph everything." },
    { icon: FileText, t: "Written, itemized quote", d: "You get a line-item written estimate with photos. No high-pressure sales. Valid 60 days." },
  ];

  const includes = [
    "Full roof walk-up (we don't just eyeball from the ground)",
    "Flashing and penetration check (chimneys, vents, skylights)",
    "Valley and ridge inspection",
    "Attic ventilation assessment",
    "Ice & water shield coverage review",
    "Gutter condition check",
    "Photo documentation of every finding",
    "Hail and wind damage scoring for insurance claims",
  ];

  return (
    <div>
      <SEO
        title="Free Roof Inspection in Oakland County, MI"
        description="Free, no-pressure roof inspection across Oakland County, MI. Walk-up roof check, photo documentation, written quote. Storm damage & insurance claim ready. Call (248) 804-6933."
        path="/free-roof-inspection"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Roof Inspection", path: "/free-roof-inspection" }])}
      />

      <section className="bg-bg-dark text-white py-16 md:py-24 relative overflow-hidden group">
        <DarkHeroAccent tone="house" />
        <div className="container-x relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-brand">Free · No pressure · No obligation</div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-3 leading-[1.02]">Free roof inspection in Oakland County, MI</h1>
            <p className="text-white/85 text-base md:text-lg mt-5 leading-relaxed max-w-xl">Think your roof has a problem? Don't guess. We'll come out, walk your roof, photograph everything, and tell you honestly whether it needs repair, partial replacement, or can safely go another 5+ years.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/get-estimate" className="btn-primary" data-testid="fri-estimate">Book inspection <ArrowRight className="w-4 h-4" /></Link>
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-inverted"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="relative bg-white/5 border border-white/15 p-7 backdrop-blur-sm">
            <RoofAccent variant="gable" position="top-right" size="lg" intensity="medium" onDark hoverOnly={false} />
            <div className="overline text-brand">Why homeowners book one</div>
            <ul className="mt-4 space-y-3">
              {["Buying a home and need to know roof age/condition", "After a big hail or wind storm", "Seeing granules in the gutter or dark streaks", "Insurance requested current roof condition", "Just wanting peace of mind"].map((r) => (
                <li key={r} className="flex gap-2 text-sm text-white/85"><CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /> {r}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24">
        <div className="container-x">
          <SectionHeading overline="How it works" title="What happens when you book a free roof inspection" sub="Four simple steps. About 45 minutes on-site. You keep the full photo report whether or not you hire us." />
          <div className="mt-10 grid md:grid-cols-4 gap-px bg-ink-line border border-ink-line">
            {steps.map((s, i) => {
              const variants = ["gable", "window", "door", "shingles"];
              return (
                <div key={s.t} className="group relative bg-white p-6 hover:bg-bg-soft transition-colors overflow-hidden">
                  <RoofAccent variant={variants[i]} position="bottom-right" size="md" intensity="soft" />
                  <div className="w-10 h-10 bg-brand/10 text-brand flex items-center justify-center rounded-sm relative"><s.icon className="w-5 h-5" /></div>
                  <div className="font-heading text-3xl font-extrabold text-brand/25 mt-2 relative">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-heading font-bold text-lg relative">{s.t}</div>
                  <div className="text-sm text-ink-soft mt-2 leading-relaxed relative">{s.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 md:py-20 bg-bg-soft border-y border-ink-line">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div>
            <div className="overline text-brand">What's included</div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">A real walk-up inspection - not a drive-by</h2>
            <p className="text-ink-soft mt-4 leading-relaxed">Many "free inspections" in Michigan are salespeople with binoculars from the ground. That's not what you get from Goral Construction. Our lead installer physically walks your roof, opens the attic hatch, and checks every penetration.</p>
            <ul className="mt-6 space-y-3">
              {includes.map((i) => (
                <li key={i} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" /><span className="text-ink leading-relaxed">{i}</span></li>
              ))}
            </ul>
          </div>
          <div className="relative bg-white p-7 border border-ink-line overflow-hidden self-start">
            <RoofAccent variant="shingles" position="bottom-right" size="xl" intensity="soft" hoverOnly={false} />
            <div className="overline text-brand">Honest policy</div>
            <h3 className="font-heading text-2xl font-extrabold mt-2 relative">If your roof has life left, we'll say so.</h3>
            <p className="text-ink-soft mt-4 leading-relaxed relative">Maciej Ogonowski has 28+ years on Michigan roofs. If your roof has 5+ years left, we'll tell you - even if it means you don't hire us today. That's why 50% of our work comes from past-customer referrals.</p>
            <Link to="/get-estimate" className="btn-primary mt-6 relative">Book my inspection <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

// ---- Standalone Certifications component (used in About + Homepage Trust bar) ----
export const CERTIFICATIONS = [
  {
    id: "shinglemaster",
    name: "ShingleMaster Credential Roofing Company",
    icon: Award,
    short: "Certified by shingle manufacturers for strict installation standards.",
    long: "Recognized for meeting strict standards in shingle installation, product knowledge, and ongoing training. This certification reflects our ability to install roofing systems according to manufacturer specifications for maximum performance and warranty protection.",
  },
  {
    id: "wizard",
    name: "Master Shingle Applicator - 'Wizard' Level",
    icon: Hammer,
    short: "Expert-level certification in asphalt shingle application and ventilation.",
    long: "Advanced certification demonstrating expert-level skills in asphalt shingle application, ventilation systems, and complex roof structures. This ensures every project is completed with precision and attention to detail.",
  },
  {
    id: "builder-license",
    name: "Individual Builder License (State of Michigan)",
    icon: Shield,
    short: "Fully licensed to perform residential construction in Michigan.",
    long: "Fully licensed to perform residential construction work, providing homeowners with confidence that all projects meet state and local building codes.",
  },
  {
    id: "velux",
    name: "VELUX Skylight & Sun Tunnel Installer",
    icon: Sun,
    short: "Certified installer of VELUX skylights and sun tunnels.",
    long: "Goral Construction LLC specializes in the professional installation of VELUX skylights and sun tunnels, bringing natural light and ventilation into your home with precision and reliability. Fixed, manual, and solar-powered venting skylights. Sun tunnels for hallways, bathrooms, and dark areas. Leak-proof flashing systems integrated with your roofing system for long-term performance.",
  },
];

export function CertificationsSection() {
  return (
    <section className="py-16 md:py-24 bg-bg-soft border-y border-ink-line">
      <div className="container-x">
        <SectionHeading overline="Credentials & certifications" title="Factory-certified, state-licensed, manufacturer-trusted" sub="These aren't decoration badges. They're audited certifications held by the owner and lead crew - which is why our installations keep full manufacturer warranties intact." />
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-line border border-ink-line">
          {CERTIFICATIONS.map((c, i) => {
            const variants = ["gable", "vane", "door", "window"];
            return (
              <div key={c.id} className="group relative bg-white p-6 md:p-7 hover:bg-bg-soft transition-colors overflow-hidden flex flex-col" data-testid={`cert-${c.id}`}>
                <RoofAccent variant={variants[i]} position="bottom-right" size="md" intensity="soft" />
                <div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm relative"><c.icon className="w-6 h-6" /></div>
                <h3 className="font-heading text-lg font-extrabold mt-5 tracking-tight relative leading-tight">{c.name}</h3>
                <p className="text-ink-soft text-sm mt-3 leading-relaxed flex-1 relative">{c.long}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
